import { useCallback, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { isNil } from 'lodash';
import SummaryDefinitionsService from 'services/summaryDefinitions';
import { throwError } from 'utilities';
import {
  OrderedDefinition,
  SUMMARY_DEFINITIONS_QUERY_KEYS,
  SummaryDefinitionListResponse,
  UseGetDefinitionsReturn,
  UseGetFirmDefinitionsParams,
} from './types';

const DEFAULT_ERROR_MESSAGE = 'Failed to fetch firm summary definitions';

/**
 * Fetch firm summary definitions from the API
 */
export const getFirmDefinitions = async (firmId: number): Promise<SummaryDefinitionListResponse | null> => {
  if (isNil(firmId)) {
    return null;
  }

  const service = new SummaryDefinitionsService();

  try {
    const response = await service.getFirmDefinitions(firmId);
    if (!response) {
      return throwError(DEFAULT_ERROR_MESSAGE);
    }
    return response;
  } catch (error) {
    return throwError(DEFAULT_ERROR_MESSAGE, error);
  }
};

/**
 * Hook to fetch firm summary definitions
 * Returns global definitions, custom definitions, and their display order
 */
const useGetFirmDefinitions = (params: UseGetFirmDefinitionsParams): UseGetDefinitionsReturn => {
  const { firmId, enabled = true } = params;

  const queryFn = useCallback(() => getFirmDefinitions(firmId), [firmId]);

  const { data, error, isError, isFetched, isFetching, isLoading, refetch } = useQuery<
    SummaryDefinitionListResponse | null,
    Error
  >({
    enabled: !isNil(firmId) && enabled,
    queryFn,
    queryKey: SUMMARY_DEFINITIONS_QUERY_KEYS.firm(firmId),
    staleTime: 30000, // 30 seconds
  });

  return {
    data: data ?? undefined,
    error,
    isError,
    isFetched,
    isFetching,
    isLoading,
    refetch,
  };
};

/**
 * Utility hook that returns definitions in display order (merged global + custom)
 */
export const useOrderedFirmDefinitions = (
  params: UseGetFirmDefinitionsParams
): {
  orderedDefinitions: OrderedDefinition[];
  isLoading: boolean;
  refetch: () => void;
} => {
  const { data, isLoading, refetch } = useGetFirmDefinitions(params);

  const orderedDefinitions = useMemo(() => {
    if (!data) {
      return [];
    }

    const { definitions, global_definitions: globalDefinitions, order } = data;

    // Create lookup maps
    const customById = new Map(definitions.map(d => [d.id, d]));
    const globalById = new Map(globalDefinitions.map(d => [d.id, d]));
    // Build ordered list
    const result: OrderedDefinition[] = [];

    // First, add items in the specified order
    order.forEach(id => {
      const customDef = customById.get(id);
      if (customDef) {
        result.push({ ...customDef, isGlobal: false });
        customById.delete(id);
      }
    });

    // Add remaining custom definitions not in order
    customById.forEach(def => {
      result.push({ ...def, isGlobal: false });
    });

    // Add global definitions (they're always visible but not in custom order)
    globalById.values().forEach(def => {
      if (!order.includes(def.id)) {
        result.push({ ...def, isGlobal: true });
      }
    });

    return result;
  }, [data]);

  return {
    orderedDefinitions,
    isLoading,
    refetch,
  };
};

export default useGetFirmDefinitions;
